import React, { useEffect, useState } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { environment } from './config/environment';
import {
  buildMsalConfigFromApi,
  B2CConfigFromApi,
} from './config/authConfig';
import App from './App';
import { LoadingSpinner } from './components/LoadingSpinner/LoadingSpinner';

type BootState =
  | { status: 'loading' }
  | { status: 'ready'; instance: PublicClientApplication }
  | { status: 'error'; message: string };

/**
 * ONE MSAL instance per page load, created outside the component.
 *
 * React 18 StrictMode double-mounts <Bootstrap/> in development, and creating the
 * PublicClientApplication inside the effect made TWO instances, each running its own
 * handleRedirectPromise. Both raced to redeem the same single-use B2C authorization code;
 * whichever instance the rendered MsalProvider happened to hold decided the outcome, so
 * the same sign-in would sometimes reach the dashboard and sometimes bounce with no
 * account ("Authentication failed") — reproduced live 2026-09-11, with every b2c-config
 * request arriving in pairs as the visible fingerprint. A module-level promise gives both
 * mounts the same fetch, the same instance, and a single redemption.
 */
let msalBootPromise: Promise<PublicClientApplication> | null = null;

function getMsalInstance(): Promise<PublicClientApplication> {
  if (!msalBootPromise) {
    msalBootPromise = (async () => {
      const res = await fetch(`${environment.apiBaseUrl}/api/auth/b2c-config`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) {
        throw new Error(`Failed to load B2C configuration from backend (HTTP ${res.status}).`);
      }
      const data: B2CConfigFromApi = await res.json();
      if (!data.enabled) {
        throw new Error('B2C authentication is disabled in backend configuration.');
      }
      const { msalConfig } = buildMsalConfigFromApi(data);
      const instance = new PublicClientApplication(msalConfig);
      await instance.initialize();
      // Process any pending B2C redirect response NOW, and stay on /login/callback while doing
      // it. v5 defaults navigateToLoginRequestUrl to true, which navigates back to whichever
      // page STARTED the redirect once processing ends — aborting the token exchange mid-
      // flight, and on the set-password flow dropping the user on /login?passwordSet=1 where
      // the purge wiped the account that had just signed in (reproduced end-to-end
      // 2026-09-12). This is the FIRST handleRedirectPromise call of the page load, so MSAL
      // caches its result and MsalProvider's own argument-less call reuses it, option included.
      await instance.handleRedirectPromise({ navigateToLoginRequestUrl: false }).catch(() => null);
      return instance;
    })().catch((e) => {
      // Allow a later mount to retry a transient failure instead of caching it forever.
      msalBootPromise = null;
      throw e;
    });
  }
  return msalBootPromise;
}

export default function Bootstrap() {
  const [state, setState] = useState<BootState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    getMsalInstance()
      .then((instance) => {
        if (!cancelled) setState({ status: 'ready', instance });
      })
      .catch((e) => {
        if (cancelled) return;
        console.error('[Bootstrap] Failed to load B2C config from API:', e);
        setState({
          status: 'error',
          message: e instanceof Error && e.message ? e.message
            : 'Unable to reach backend to load authentication configuration.',
        });
      });

    return () => { cancelled = true; };
  }, []);

  if (state.status === 'ready') {
    return (
      <MsalProvider instance={state.instance}>
        <App />
      </MsalProvider>
    );
  }

  if (state.status === 'error') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: '2rem' }}>
        <div style={{ maxWidth: 480, textAlign: 'center' }}>
          <h3>Authentication unavailable</h3>
          <p>{state.message}</p>
        </div>
      </div>
    );
  }

  // Branded full-screen loader for the config-fetch + MSAL-init phase. This is what
  // renders during a B2C redirect return (/login?state=...) before MsalProvider mounts
  // and handleRedirectPromise resolves — shares LoadingSpinner with the rest of the app
  // and stays in sync with #app-initial-loader in public/index.html so there is no flash
  // when React takes over the initial paint.
  return <LoadingSpinner message="Connecting to Facilon Services…" />;
}
